import React, { useMemo } from 'react';
import { Autocomplete, TextField, TextFieldProps } from '@mui/material';
import { State } from '#types';
import useErrorScrollContext from '../../hooks/useErrorScrollContext';

type StateOption = {
	label: string;
	value: State;
};

const US_OPTIONS: StateOption[] = [
	{ label: 'Alabama', value: State.AL },
	{ label: 'Alaska', value: State.AK },
	{ label: 'Arizona', value: State.AZ },
	{ label: 'Arkansas', value: State.AR },
	{ label: 'California', value: State.CA },
	{ label: 'Colorado', value: State.CO },
	{ label: 'Connecticut', value: State.CT },
	{ label: 'Delaware', value: State.DE },
	{ label: 'District of Columbia', value: State.DC },
	{ label: 'Florida', value: State.FL },
	{ label: 'Georgia', value: State.GA },
	{ label: 'Hawaii', value: State.HI },
	{ label: 'Idaho', value: State.ID },
	{ label: 'Illinois', value: State.IL },
	{ label: 'Indiana', value: State.IN },
	{ label: 'Iowa', value: State.IA },
	{ label: 'Kansas', value: State.KS },
	{ label: 'Kentucky', value: State.KY },
	{ label: 'Louisiana', value: State.LA },
	{ label: 'Maine', value: State.ME },
	{ label: 'Maryland', value: State.MD },
	{ label: 'Massachusetts', value: State.MA },
	{ label: 'Michigan', value: State.MI },
	{ label: 'Minnesota', value: State.MN },
	{ label: 'Mississippi', value: State.MS },
	{ label: 'Missouri', value: State.MO },
	{ label: 'Montana', value: State.MT },
	{ label: 'Nebraska', value: State.NE },
	{ label: 'Nevada', value: State.NV },
	{ label: 'New Hampshire', value: State.NH },
	{ label: 'New Jersey', value: State.NJ },
	{ label: 'New Mexico', value: State.NM },
	{ label: 'New York', value: State.NY },
	{ label: 'North Carolina', value: State.NC },
	{ label: 'North Dakota', value: State.ND },
	{ label: 'Ohio', value: State.OH },
	{ label: 'Oklahoma', value: State.OK },
	{ label: 'Oregon', value: State.OR },
	{ label: 'Pennsylvania', value: State.PA },
	{ label: 'Puerto Rico', value: State.PR },
	{ label: 'Rhode Island', value: State.RI },
	{ label: 'South Carolina', value: State.SC },
	{ label: 'South Dakota', value: State.SD },
	{ label: 'Tennessee', value: State.TN },
	{ label: 'Texas', value: State.TX },
	{ label: 'Utah', value: State.UT },
	{ label: 'Vermont', value: State.VT },
	{ label: 'Virginia', value: State.VA },
	{ label: 'Washington', value: State.WA },
	{ label: 'West Virginia', value: State.WV },
	{ label: 'Wisconsin', value: State.WI },
	{ label: 'Wyoming', value: State.WY },
];

const CANADA_OPTIONS: StateOption[] = [
	{ label: 'Alberta', value: State.AB },
	{ label: 'British Columbia', value: State.BC },
	{ label: 'Manitoba', value: State.MB },
	{ label: 'New Brunswick', value: State.NB },
	{ label: 'Newfoundland and Labrador', value: State.NL },
	{ label: 'Northwest Territories', value: State.NT },
	{ label: 'Nova Scotia', value: State.NS },
	{ label: 'Nunavut', value: State.NU },
	{ label: 'Ontario', value: State.ON },
	{ label: 'Prince Edward Island', value: State.PE },
	// filter out Quebec because we don't offer French translations yet
	//{ label: 'Quebec', value: State.QC },
	{ label: 'Saskatchewan', value: State.SK },
	{ label: 'Yukon', value: State.YT },
];

export const CANADA_STATE_CODES: readonly State[] = CANADA_OPTIONS.map(
	( option ) => option.value
);

export const US_STATE_CODES: readonly State[] = US_OPTIONS.map(
	( option ) => option.value
);

type StateSelectProps = {
	id?: string;
	className?: string;
	label: string;
	onChange: (
		event: React.SyntheticEvent<Element, Event>,
		value: string | undefined
	) => void;
	value?: string;
	country?: string;
	error: boolean;
	helperText?: string;
	onBlur?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
	textFieldSlotProps?: TextFieldProps['slotProps'];
	disabled?: boolean;
	variant?: TextFieldProps['variant'];
};

const getOptionsForCountry = ( country?: string ): StateOption[] => {
	if ( country === 'US' ) {
		return US_OPTIONS;
	}
	if ( country === 'CA' ) {
		return CANADA_OPTIONS;
	}
	return [ ...US_OPTIONS, ...CANADA_OPTIONS ];
};

const StateSelect: React.FC<StateSelectProps> = ( {
	id,
	className,
	label,
	onChange,
	value,
	country,
	error,
	helperText,
	onBlur,
	textFieldSlotProps,
	disabled,
	variant,
} ) => {
	const options = useMemo( () => getOptionsForCountry( country ), [ country ] );

	const normalizedValue = value?.toUpperCase();
	const selectedValue = options.find(
		( option ) => option.value === normalizedValue
	);

	const ref = useErrorScrollContext( !!error );

	const autocompleteValue = selectedValue ?? null;

	return (
		<Autocomplete<StateOption, false, undefined, false>
			ref={ ref }
			id={ id }
			className={ className }
			onChange={ ( e, newValue ) => {
				if ( !newValue ) {
					onChange?.( e, undefined );
				} else {
					onChange?.( e, newValue.value );
				}
			} }
			options={ options }
			value={ autocompleteValue }
			forcePopupIcon={ true }
			disabled={ disabled }
			getOptionLabel={ ( option ) => option?.label ?? '' }
			renderInput={ ( params ) => (
				<TextField
					{ ...params }
					fullWidth={ true }
					variant={ variant ? variant : 'outlined' }
					label={ label }
					error={ error }
					helperText={ helperText }
					onBlur={ onBlur }
					slotProps={ {
						...params.slotProps,
						...textFieldSlotProps,
						htmlInput: {
							...params.slotProps?.htmlInput,
							...textFieldSlotProps?.htmlInput,
						},
					} }
				/>
			) }
		/>
	);
};

export default StateSelect;
